import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  useBulkProcessConsignmentsMutation,
  useChangeConsignmentStatusMutation,
  type ConsignmentStatusAction,
  type ConsignmentSummary
} from '@/entities/consignments';
import { getApiErrorMessage, useProjectSiteNavigation } from '@/shared/lib';

import { processBulkResult } from '../lib/bulk-process-utils';
import { RouteNames } from '@/shared/config/routes';
import {
  ACTION_TARGET_STATUS,
  type ConsignmentStatusChangeValues,
  type ConsignmentStatusTarget
} from './status-actions';

/** What a status action needs from a row: its id, and the status its reset would restore. */
type StatusChangeRow = Pick<ConsignmentSummary, 'id' | 'resetTargetStatusCode'>;

interface PendingStatusChange extends ConsignmentStatusTarget {
  ids: string[];
}

interface UseConsignmentActionsProps {
  selectedIds?: string[];
  onClearSelection?: () => void;
}

/**
 * Consignment actions of the list screen. A status change of one consignment goes through
 * `POST /consignments/{id}/status`, several through `bulk-process` — the backend recommends the single endpoint
 * whenever there is one id, since it answers with the updated consignment.
 */
export function useConsignmentActions({ selectedIds = [], onClearSelection }: UseConsignmentActionsProps = {}) {
  const { t } = useTranslation();
  const { projectId, siteId, navigateWithContext } = useProjectSiteNavigation();
  const [bulkProcess, { isLoading: isBulkProcessing }] = useBulkProcessConsignmentsMutation();
  const [changeStatus, { isLoading: isChangingStatus }] = useChangeConsignmentStatusMutation();

  const [changePriorityOpen, setChangePriorityOpen] = useState(false);
  const [showDispositionOpen, setShowDispositionOpen] = useState(false);
  const [statusChangeOpen, setStatusChangeOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<PendingStatusChange | null>(null);

  const [localSelectedIds, setLocalSelectedIds] = useState<string[] | null>(null);

  const handleEditView = useCallback(
    (ids?: string[]) => {
      const consignmentIds = ids && ids.length > 0 ? ids : selectedIds;
      if (consignmentIds.length > 0) {
        navigateWithContext(
          RouteNames.CONSIGNMENT_EDIT,
          { consignmentId: consignmentIds[0] },
          {
            state: { consignmentIds }
          }
        );
      }
    },
    [selectedIds, navigateWithContext]
  );

  const handleShowDisposition = useCallback((ids?: string[]) => {
    const idsToUse = ids && ids.length > 0 ? ids : null;
    setLocalSelectedIds(idsToUse);
    setShowDispositionOpen(true);
  }, []);

  const handleChangePriority = useCallback((ids?: string[]) => {
    const idsToUse = ids && ids.length > 0 ? ids : null;
    setLocalSelectedIds(idsToUse);
    setChangePriorityOpen(true);
  }, []);

  const handleChangeStatus = useCallback((action: ConsignmentStatusAction, rows: StatusChangeRow[]) => {
    if (rows.length === 0) return;

    const resetTargets = new Set(rows.map((row) => row.resetTargetStatusCode).filter(Boolean));
    setPendingStatusChange({
      action,
      statusCode: ACTION_TARGET_STATUS[action] ?? (resetTargets.size === 1 ? [...resetTargets][0]! : null),
      ids: rows.map((row) => row.id)
    });
    setStatusChangeOpen(true);
  }, []);

  const confirmChangePriority = useCallback(
    async (priority: string, comments: string) => {
      if (!projectId || !siteId) {
        toast.error(t('consignment.edit.missingParams', 'Missing project or site parameters'));
        return;
      }
      const consignmentIds = localSelectedIds || selectedIds;

      try {
        const result = await bulkProcess({
          projectId,
          siteId,
          consignmentIds,
          action: 'CHANGE_PRIORITY',
          payload: {
            priority,
            comment: comments
          }
        }).unwrap();

        processBulkResult({
          result,
          successMessage: (count) => `Priority changed for ${count} consignment(s)`,
          errorMessage: 'Failed to change priority for all selected consignments',
          warningMessage: (succeeded, total) => `Priority changed for ${succeeded} of ${total} consignments`
        });

        onClearSelection?.();
        setChangePriorityOpen(false);
        setLocalSelectedIds(null);
      } catch (error) {
        toast.error(getApiErrorMessage(error, t('newcmr.save.error')));
      }
    },
    [projectId, siteId, localSelectedIds, selectedIds, bulkProcess, onClearSelection, t]
  );

  const confirmChangeStatus = useCallback(
    async ({ reason, comment }: ConsignmentStatusChangeValues) => {
      if (!pendingStatusChange) return;
      if (!projectId || !siteId) {
        toast.error(t('consignment.edit.missingParams', 'Missing project or site parameters'));
        return;
      }

      const { action, ids } = pendingStatusChange;
      const isCancellation = action === 'CANCEL';

      try {
        if (ids.length === 1) {
          await changeStatus({
            id: ids[0],
            projectId,
            siteId,
            action,
            ...(isCancellation && { cancellationReason: reason }),
            comment
          }).unwrap();
          toast.success(t('newcmr.save.success'));
        } else {
          const result = await bulkProcess({
            projectId,
            siteId,
            consignmentIds: ids,
            action,
            payload: {
              ...(isCancellation && { reason }),
              comment
            }
          }).unwrap();

          processBulkResult({
            result,
            successMessage: () => t('newcmr.save.success'),
            errorMessage: t('newcmr.save.error'),
            warningMessage: (succeeded, total) => `${t('newcmr.save.success')} (${succeeded}/${total})`
          });
        }

        onClearSelection?.();
        setStatusChangeOpen(false);
      } catch (error) {
        toast.error(getApiErrorMessage(error, t('newcmr.save.error')));
      }
    },
    [pendingStatusChange, projectId, siteId, changeStatus, bulkProcess, onClearSelection, t]
  );

  return {
    actions: {
      editView: handleEditView,
      showDisposition: handleShowDisposition,
      changePriority: handleChangePriority,
      changeStatus: handleChangeStatus
    },
    dialogs: {
      showDisposition: {
        open: showDispositionOpen,
        onOpenChange: setShowDispositionOpen,
        consignmentId: localSelectedIds?.[0] || selectedIds?.[0] || null
      },
      changePriority: {
        open: changePriorityOpen,
        onOpenChange: setChangePriorityOpen,
        onConfirm: confirmChangePriority
      },
      statusChange: {
        open: statusChangeOpen,
        onOpenChange: setStatusChangeOpen,
        target: pendingStatusChange,
        onConfirm: confirmChangeStatus
      }
    },
    selectedCount: selectedIds.length,
    isLoading: isBulkProcessing || isChangingStatus
  };
}
